import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { useMiseSellerGroups, useMiseSellers } from '@/lib/query/hooks/useMiseAccounts';
import { useUpdateSellersNext } from '@/lib/query/hooks/useMiseSellers';
import { EXCLUDED_SELLER_ID } from '@/lib/types/mise/seller';
import type { MiseSeller } from '@/lib/types/mise/seller';
import { buildSellerTreeOptions } from '@/lib/mise/sellerTree';
import { SellerTreeCheckboxList } from '@/components/mise/SellerTreeCheckboxList';

interface SellersNextEditDialogProps {
  open: boolean;
  onClose: () => void;
  accountId: string;
  currentSellersNext: MiseSeller[] | undefined;
  /** 集計後（current）の店舗。集計前（next）が未設定のときの初期選択フォールバックに使う */
  currentSellers: MiseSeller[] | undefined;
}

export function SellersNextEditDialog({
  open,
  onClose,
  accountId,
  currentSellersNext,
  currentSellers,
}: SellersNextEditDialogProps) {
  const [selectedSellerIds, setSelectedSellerIds] = useState<Set<string>>(new Set());
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  // 開くたびに一度だけ初期化するためのフラグ（開いている間の再フェッチによる選択リセットを防ぐ）
  const hasInitializedRef = useRef(false);

  // 店舗一覧を取得（snapshot_hot.sellers）
  const {
    data: sellers,
    isLoading: sellersLoading,
    error: sellersError,
  } = useMiseSellers(accountId);

  // 中央倉庫を除外した店舗一覧
  const filteredSellers = useMemo(
    () => sellers?.filter((seller) => seller.id !== EXCLUDED_SELLER_ID),
    [sellers],
  );

  // 同名の店舗をどのグループのものか見分けられるよう、店舗グループ階層の下に並べる
  const {
    data: sellerGroupNodes,
    isLoading: sellerGroupNodesLoading,
    error: sellerGroupNodesError,
  } = useMiseSellerGroups(accountId);
  const sellerTreeOptions = useMemo(
    () => buildSellerTreeOptions(filteredSellers ?? [], sellerGroupNodes ?? [], sellers ?? []),
    [filteredSellers, sellerGroupNodes, sellers],
  );
  const listLoading = sellersLoading || sellerGroupNodesLoading;

  // 更新Mutation
  const updateMutation = useUpdateSellersNext(accountId);

  // 初期値の設定
  // 集計前（next）が設定済みならそれを、未設定（空）なら集計後（current）を初期選択にする。
  // これにより「今登録してある店舗」がチェックON状態でダイアログが開き、追加店舗のみ選べばよくなる。
  // 開いている間の再フェッチで選択がリセットされないよう、開くたびに一度だけ初期化する。
  useEffect(() => {
    if (!open) {
      hasInitializedRef.current = false;
      return;
    }
    if (hasInitializedRef.current) return;
    // next が未取得の間は next を優先するため初期化を保留する
    if (currentSellersNext === undefined) return;
    // next が空なら current にフォールバック。current も未取得なら保留する。
    if (currentSellersNext.length === 0 && currentSellers === undefined) return;
    const base = currentSellersNext.length > 0 ? currentSellersNext : (currentSellers ?? []);
    setSelectedSellerIds(new Set(base.map((seller) => seller.id)));
    hasInitializedRef.current = true;
  }, [open, currentSellersNext, currentSellers]);

  // ダイアログを閉じる際に状態をリセット
  const handleClose = () => {
    if (!updateMutation.isPending) {
      setConfirmDialogOpen(false);
      onClose();
    }
  };

  // チェックボックスの変更処理
  const handleToggleSeller = (sellerId: string) => {
    setSelectedSellerIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sellerId)) {
        newSet.delete(sellerId);
      } else {
        newSet.add(sellerId);
      }
      return newSet;
    });
  };

  // グループ配下の店舗をまとめて選択/解除
  const handleToggleSellers = (sellerIds: string[], selected: boolean) => {
    setSelectedSellerIds((prev) => {
      const newSet = new Set(prev);
      sellerIds.forEach((id) => (selected ? newSet.add(id) : newSet.delete(id)));
      return newSet;
    });
  };

  // 全選択/全解除
  const handleSelectAll = () => {
    if (filteredSellers && selectedSellerIds.size === filteredSellers.length) {
      // 全解除
      setSelectedSellerIds(new Set());
    } else {
      // 全選択
      if (filteredSellers) {
        setSelectedSellerIds(new Set(filteredSellers.map((seller) => seller.id)));
      }
    }
  };

  // 保存ボタンクリック時の処理
  const handleSave = () => {
    // 確認ダイアログを表示
    setConfirmDialogOpen(true);
  };

  // 確認ダイアログで確定した場合の処理
  const handleConfirmSave = async () => {
    setConfirmDialogOpen(false);

    const request = {
      seller_ids: Array.from(selectedSellerIds),
    };

    try {
      await updateMutation.mutateAsync(request);
      onClose();
    } catch (error) {
      // エラーはmutationのエラーとして表示される
      console.error('Failed to update sellers next:', error);
    }
  };

  const isAllSelected = filteredSellers && selectedSellerIds.size === filteredSellers.length;
  const isIndeterminate =
    filteredSellers &&
    selectedSellerIds.size > 0 &&
    selectedSellerIds.size < filteredSellers.length;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>集計前の店舗を編集</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {listLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>店舗一覧を読み込み中...</Typography>
              </Box>
            ) : sellersError ? (
              <Alert severity="error" sx={{ mt: 1 }}>
                店舗一覧の取得に失敗しました
              </Alert>
            ) : sellerGroupNodesError ? (
              // 階層が無いと全店舗が「グループ未所属」に並び、同名の店舗を見分けられないまま選ばせてしまう
              <Alert severity="error" sx={{ mt: 1 }}>
                店舗グループの取得に失敗したため、店舗を階層で表示できません。時間をおいて開き直してください
              </Alert>
            ) : !filteredSellers || filteredSellers.length === 0 ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                店舗が登録されていません
              </Alert>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    店舗を選択してください（{selectedSellerIds.size}件選択中）
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={isIndeterminate}
                        onChange={handleSelectAll}
                      />
                    }
                    label={isAllSelected ? '全解除' : '全選択'}
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />
                <SellerTreeCheckboxList
                  options={sellerTreeOptions}
                  selectedIds={selectedSellerIds}
                  onToggleSeller={handleToggleSeller}
                  onToggleSellers={handleToggleSellers}
                />
              </>
            )}

            {updateMutation.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {updateMutation.error instanceof Error
                  ? updateMutation.error.message
                  : '集計前の店舗一覧の更新に失敗しました'}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={updateMutation.isPending}>
            キャンセル
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              updateMutation.isPending ||
              listLoading ||
              !!sellerGroupNodesError ||
              !filteredSellers ||
              filteredSellers.length === 0
            }
          >
            {updateMutation.isPending ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 確認ダイアログ */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-container': {
            zIndex: 1400, // Material-UIのDialogより高いz-indexを設定
          },
        }}
      >
        <DialogTitle>集計前の店舗一覧を更新しますか？</DialogTitle>
        <DialogContent>
          <Typography>
            選択した{selectedSellerIds.size}件の店舗が集計前の店舗一覧に設定されます。
            この操作により、既存の集計前の店舗一覧は上書きされます。よろしいですか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={updateMutation.isPending}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmSave}
            variant="contained"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? '更新中...' : '更新する'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
